import React, { useState, useMemo } from "react";
import { useProcurementUser } from "../context/ProcurementUserContext";
import ProcurementUserSwitcher from "../components/ProcurementUserSwitcher";

const API = process.env.REACT_APP_API_URL || "";

const C = {
  bg: "#0F1117", card: "#161C2E", cardBorder: "#222A40",
  gold: "#C9972B", text: "#F4F0E8", textSec: "#9EA8C0", textDim: "#5C657A",
  red: "#EF4444", green: "#22C55E", amber: "#F59E0B", blue: "#3B82F6",
  teal: "#14B8A6", orange: "#F97316", rule: "#2A3048",
};

const STATUS_COLORS = {
  not_started: "#64748B", in_production: C.blue, ready_to_ship: C.amber,
  shipped: C.orange, in_storage: "#8B5CF6", delivered: C.teal,
  installed: C.green, commissioned: "#22D3EE", ops_turnover: "#10B981",
};

const today = new Date(); today.setHours(0,0,0,0);
const in30 = new Date(today); in30.setDate(in30.getDate() + 30);

function milestoneColor(d) {
  if (!d) return C.textDim;
  const dt = new Date(d); dt.setHours(0,0,0,0);
  if (dt < today) return C.green;
  if (dt <= in30) return C.amber;
  return "#64748B";
}

function Badge({ text, color }) {
  return <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, fontFamily: "monospace", background: `${color}22`, color, border: `1px solid ${color}44` }}>{text?.replace(/_/g, " ")}</span>;
}
function Toast({ msg, onClose }) {
  if (!msg) return null;
  return <div style={{ position: "fixed", bottom: 24, right: 24, background: C.green, color: "#fff", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontFamily: "monospace", zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>{msg}<button onClick={onClose} style={{ marginLeft: 12, background: "none", border: "none", color: "#fff", cursor: "pointer", fontWeight: 700 }}>x</button></div>;
}

export default function CxLookAhead() {
  const { activeUser } = useProcurementUser();
  const hdrs = { "Content-Type": "application/json", "X-User": activeUser.identifier };

  const [lookaheadDays, setLookaheadDays] = useState(90);
  const [allocationFilter, setAllocationFilter] = useState("");
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [generatedAt, setGeneratedAt] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setGenerating(true); setError(null); setResult(null);
    const params = { lookahead_days: lookaheadDays, tenant_id: 1 };
    if (allocationFilter.trim()) params.allocation = allocationFilter.trim();
    try {
      const r = await fetch(`${API}/procurement/chat`, { method: "POST", headers: hdrs, body: JSON.stringify({ tool: "generate_cx_lookahead", tool_params: params, message: "" }) });
      const d = await r.json();
      setResult(d.result || d);
      setGeneratedAt(new Date());
      setCollapsedGroups(new Set());
    } catch (e) { setError(e.message); }
    setGenerating(false);
  };

  const toggleGroup = (alloc) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.has(alloc) ? next.delete(alloc) : next.add(alloc);
      return next;
    });
  };

  const copyNarrative = () => {
    if (result?.narrative) {
      navigator.clipboard.writeText(result.narrative);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const exportCsv = () => {
    if (!result?.schedule?.length) return;
    const cols = ["allocation","build_phase_seq","equipment_type","vendor_name","mw","production_status","contractual_date","roj_execution","delivery_first_dest"];
    const csv = [cols.join(","), ...result.schedule.map(r => cols.map(c => `"${(r[c] ?? "").toString().replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `cx-lookahead-${new Date().toISOString().slice(0,10)}-${lookaheadDays}d.csv`; a.click();
  };

  const groupedSchedule = useMemo(() => {
    if (!result?.schedule) return {};
    const g = {};
    for (const r of result.schedule) {
      const k = r.allocation || "UNASSIGNED";
      if (!g[k]) g[k] = { rows: [], project_name: r.project_name, mw: r.mw };
      g[k].rows.push(r);
    }
    for (const k of Object.keys(g)) {
      g[k].rows.sort((a, b) => (a.build_phase_seq || "").localeCompare(b.build_phase_seq || "") || (a.equipment_type || "").localeCompare(b.equipment_type || ""));
    }
    return g;
  }, [result]);

  const inp = { background: C.bg, border: `1px solid ${C.cardBorder}`, color: C.text, borderRadius: 8, padding: "6px 10px", fontSize: 12, fontFamily: "monospace", outline: "none" };
  const sel = { ...inp, cursor: "pointer" };
  const btn = { background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "8px 20px", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "monospace" };
  const th = { padding: "8px 10px", fontSize: 11, fontFamily: "monospace", textTransform: "uppercase", color: C.textDim, whiteSpace: "nowrap", borderBottom: `1px solid ${C.rule}`, textAlign: "left", letterSpacing: "0.06em" };
  const td = { padding: "6px 10px", fontSize: 12, borderBottom: `1px solid ${C.rule}`, fontFamily: "monospace" };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Outfit', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1440, margin: "0 auto", padding: "16px 24px 48px" }}>

        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, borderBottom: `1px solid ${C.rule}`, paddingBottom: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Cx Look Ahead</h1>
            <div style={{ fontSize: 12, color: C.textSec }}>Monthly commissioning schedule — {lookaheadDays}-day window</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <ProcurementUserSwitcher />
            <button onClick={generate} disabled={generating} style={{ ...btn, background: generating ? C.textDim : C.amber }}>
              {generating ? "Generating..." : "Generate Look Ahead"}
            </button>
            {result && <button onClick={generate} style={{ ...btn, background: C.card, color: C.textSec, border: `1px solid ${C.cardBorder}`, fontSize: 12, padding: "6px 14px" }}>Refresh</button>}
          </div>
        </div>

        {/* CONTROLS */}
        <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={lookaheadDays} onChange={e => setLookaheadDays(Number(e.target.value))} style={sel}>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
          </select>
          <input value={allocationFilter} onChange={e => setAllocationFilter(e.target.value)} placeholder="Allocation filter (optional)" style={{ ...inp, width: 220 }} />
        </div>
        <div style={{ fontSize: 11, color: C.textDim, marginBottom: 16 }}>
          This report shows scheduled Cx milestones for all active projects in the selected window. Data backfeeds to Production Planners automatically so vendors always see current field timing.
        </div>

        {/* GENERATING */}
        {generating && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ fontSize: 28, marginBottom: 12, animation: "spin 1s linear infinite" }}>&#x23F3;</div>
            <div style={{ fontSize: 15, color: C.gold }}>Generating Cx Look Ahead...</div>
            <div style={{ fontSize: 12, color: C.textDim, marginTop: 8 }}>Analyzing {lookaheadDays}-day schedule window...</div>
          </div>
        )}

        {/* ERROR */}
        {error && !generating && (
          <div style={{ background: `${C.amber}11`, border: `1px solid ${C.amber}33`, borderRadius: 12, padding: "20px 24px", textAlign: "center" }}>
            <div style={{ color: C.amber, fontSize: 14, marginBottom: 8 }}>{error}</div>
            <button onClick={generate} style={btn}>Try Again</button>
          </div>
        )}

        {/* PRE-GENERATE */}
        {!result && !generating && !error && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#x1F4C5;</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Generate your Cx Look Ahead</div>
            <div style={{ fontSize: 13, color: C.textSec, maxWidth: 480, margin: "0 auto 20px" }}>
              Click Generate Look Ahead above to produce an AI-synthesized commissioning schedule for the selected window. Results include a narrative summary and detailed milestone table.
            </div>
            <button onClick={generate} style={btn}>Generate Look Ahead</button>
          </div>
        )}

        {/* RESULTS */}
        {result && !generating && (
          <>
            {/* AI NARRATIVE */}
            {result.narrative && (
              <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderLeft: `4px solid ${C.gold}`, borderRadius: 12, padding: "24px 28px", marginBottom: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}>
                <div style={{ fontSize: 10, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.12em", color: C.textDim, marginBottom: 12 }}>
                  AI LOOK AHEAD SUMMARY — {generatedAt?.toLocaleDateString()}
                </div>
                <div style={{ fontSize: "1.05rem", lineHeight: 1.9, color: C.text, fontStyle: "italic" }}>
                  {result.narrative}
                </div>
                <div style={{ display: "flex", gap: 32, marginTop: 20, borderTop: `1px solid ${C.rule}`, paddingTop: 16 }}>
                  {[
                    { n: result.item_count, label: "items scheduled" },
                    { n: result.project_count, label: "projects" },
                    { n: result.vendor_count, label: "vendors" },
                  ].map((s, i) => (
                    <div key={i}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: C.gold, fontFamily: "monospace" }}>{s.n}</div>
                      <div style={{ fontSize: 11, color: C.textDim }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* NO ITEMS MESSAGE */}
            {result.item_count === 0 && (
              <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "24px 28px", marginBottom: 16, textAlign: "center" }}>
                <div style={{ fontSize: 14, color: C.textSec }}>{result.message || "No scheduled deliveries in this window."}</div>
                <div style={{ fontSize: 12, color: C.textDim, marginTop: 6 }}>Try extending the lookahead window or running a schedule sync from the Project Schedule page.</div>
              </div>
            )}

            {/* SCHEDULE TABLE */}
            {result.schedule?.length > 0 && (
              <>
                {/* EXPORT + TITLE */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.gold, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                    Schedule Detail — {result.item_count} items
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={copyNarrative} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 6, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontFamily: "monospace" }}>
                      {copied ? "Copied \u2713" : "Copy Narrative"}
                    </button>
                    <button onClick={exportCsv} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 6, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontFamily: "monospace" }}>Export CSV</button>
                  </div>
                </div>

                <div style={{ overflowX: "auto", borderRadius: 12, border: `1px solid ${C.cardBorder}` }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", background: C.card }}>
                    <thead>
                      <tr>
                        <th style={th}>Allocation</th>
                        <th style={th}>Phase</th>
                        <th style={th}>Equipment</th>
                        <th style={th}>Vendor</th>
                        <th style={{ ...th, textAlign: "right" }}>MW</th>
                        <th style={th}>Status</th>
                        <th style={th}>Contract Date</th>
                        <th style={th}>ROJ Exec</th>
                        <th style={th}>Delivery Dest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(groupedSchedule).map(([alloc, group]) => {
                        const collapsed = collapsedGroups.has(alloc);
                        return (
                          <React.Fragment key={alloc}>
                            <tr onClick={() => toggleGroup(alloc)} style={{ background: `${C.rule}66`, cursor: "pointer" }}>
                              <td colSpan={8} style={{ ...td, fontWeight: 700, color: C.gold, fontSize: 13, borderBottom: `1px solid ${C.rule}` }}>
                                {collapsed ? "\u25B6" : "\u25BC"} {alloc} — {group.project_name || ""}
                              </td>
                              <td style={{ ...td, textAlign: "right", fontWeight: 600, color: C.textSec, fontSize: 12 }}>
                                {group.mw ? `${group.mw} MW` : ""}
                              </td>
                            </tr>
                            {!collapsed && group.rows.map((r, i) => (
                              <tr key={i}>
                                <td style={{ ...td, color: C.amber }}>{r.allocation}</td>
                                <td style={td}>{r.build_phase_seq || "—"}</td>
                                <td style={td}>{r.equipment_type || "—"}</td>
                                <td style={{ ...td, color: C.textSec }}>{r.vendor_name || "—"}</td>
                                <td style={{ ...td, textAlign: "right" }}>{r.mw || "—"}</td>
                                <td style={td}><Badge text={r.production_status} color={STATUS_COLORS[r.production_status] || "#64748B"} /></td>
                                <td style={{ ...td, color: milestoneColor(r.contractual_date) }}>{r.contractual_date || "—"}</td>
                                <td style={{ ...td, color: milestoneColor(r.roj_execution) }}>{r.roj_execution || "—"}</td>
                                <td style={td}>{r.delivery_first_dest || "—"}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* VENDOR CONTEXT NOTE */}
            <div style={{ background: `${C.rule}33`, border: `1px solid ${C.cardBorder}`, borderRadius: 8, padding: "10px 16px", marginTop: 16, fontSize: 12, color: C.textSec }}>
              <span style={{ marginRight: 6 }}>&#x1F4CB;</span>
              This Look Ahead is shared with vendors to help them track field timing. Data shown here backfeeds into Production Planners automatically — vendors can always see where their equipment is heading in the field.
            </div>
          </>
        )}
      </div>
      <Toast msg={toast} onClose={() => setToast(null)} />
    </div>
  );
}
